import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiArrowRight, FiCheckCircle, FiZap, FiShield } = FiIcons;

const SolutionSection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2 mb-6">
            <SafeIcon icon={FiShield} className="text-green-600" />
            <span className="text-sm font-semibold text-green-700">Your Intelligent Gatekeeper</span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">24/7</span> Intelligent Gatekeeper
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Introducing the <span className="font-bold text-gray-900">Handvantage AI Assistant</span>. It's not just a booking tool. 
            It's a custom-trained AI that we build and deploy for you. It acts as your intelligent front desk, engaging every website visitor, 
            asking the right qualifying questions, and only booking strategy calls with your ideal, high-intent clients.
          </p>
          <p className="text-2xl font-bold text-blue-600">
            Imagine waking up to a calendar full of pre-qualified, high-ticket prospects who are ready to invest in your services.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Before/After Comparison */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {/* Before */}
            <div className="bg-white p-8 rounded-2xl border-2 border-red-200 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 rounded-full p-3">
                  <span className="text-3xl">😰</span>
                </div>
                <h3 className="text-2xl font-bold text-red-800">Stressed-Out Coach</h3>
              </div>
              
              <ul className="space-y-3">
                {[
                  'On a messy discovery call',
                  'With an unqualified prospect',
                  'Who "needs to think about it"',
                  'Wasting another hour',
                  'Feeling burned out'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <span className="text-red-500 mt-1">❌</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* After */}
            <div className="bg-white p-8 rounded-2xl border-2 border-green-200 shadow-xl relative">
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg rotate-12">
                ⭐ YOU
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 rounded-full p-3">
                  <span className="text-3xl">😌</span>
                </div>
                <h3 className="text-2xl font-bold text-green-800">Confident Coach</h3>
              </div>
              
              <ul className="space-y-3">
                {[
                  'Having a productive conversation',
                  'With a perfectly matched client',
                  'Who\'s ready to invest',
                  'Closing high-ticket deals',
                  'Feeling energized'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <span className="text-green-500 mt-1">✅</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Features Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white p-10 rounded-3xl shadow-2xl border border-blue-100 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <SafeIcon icon={FiUsers} className="text-4xl text-blue-600" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                What Makes It Different?
              </h3>
              
              <div className="space-y-4 mb-8">
                {[
                  {
                    title: 'Custom-Trained',
                    desc: 'Built specifically for your coaching methodology'
                  },
                  {
                    title: 'Intelligent Filtering',
                    desc: 'Asks the right qualifying questions automatically'
                  },
                  {
                    title: 'Instant Engagement',
                    desc: 'Responds in under 2 seconds, 24/7'
                  },
                  {
                    title: 'Direct Booking',
                    desc: 'Only qualified prospects get on your calendar'
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg">
                    <SafeIcon icon={FiCheckCircle} className="text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-gray-900">{feature.title}</p>
                      <p className="text-sm text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-8 py-5 rounded-xl font-bold w-full hover:shadow-2xl transition-all duration-300 text-lg group"
              >
                SEE HOW IT WORKS
                <SafeIcon icon={FiArrowRight} className="inline-block ml-2 group-hover:translate-x-2 transition-transform" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;